import { SELECTED_TIMEZONE_LOCALSTORAGE_KEY } from '@/constant/global/global.constant';
import moment from 'moment-timezone';

export enum EAppTimezones {
  UTC = 'UTC',
  EST = 'America/New_York',
  CST = 'America/Chicago',
  MST = 'America/Denver',
  PST = 'America/Los_Angeles',
  IST = 'Asia/Kolkata',
  CHINA_CST = 'Asia/Shanghai',
}

/**
 * Formats a given time string to the selected timezone.
 *
 * @param timezone - Selected timezone from EAppTimezones enum.
 * @param timeString - Time string to be formatted (optional).
 * @returns Formatted time string or a hyphen '-' if time is not provided or invalid.
 */
export function formatTimeByTimezone(
  timezone: EAppTimezones = EAppTimezones.EST,
  timeString?: string,
): string {
  if (!timeString || !moment(timeString, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  // return moment.tz(timeString, timezone).format("lll z")
  return moment.tz(timeString, timezone).format('MMM DD, YYYY h:mm:ss A z');
}

/**
 * Formats a given time string to the selected timezone.
 *
 * @param timezone - Selected timezone from EAppTimezones enum.
 * @param timeString - Time string to be formatted (optional).
 * @returns Formatted time string or a hyphen '-' if time is not provided or invalid.
 */
export function formatTimeAgoByTimezone(
  timezone: EAppTimezones = EAppTimezones.EST,
  timeString?: string,
): string {
  if (!timeString || !moment(timeString, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  // return moment.tz(timeString, timezone).format("lll z")
  return moment.tz(timeString, timezone).fromNow();
}

/**
 * Formats a date to the selected timezone or returns '-' if date is not valid or not provided.
 *
 * @param date - Date string or Date object to be formatted.
 * @returns Formatted date string or a hyphen '-' if date is invalid or not provided.
 */
export const getDateTime = (date?: string | Date): string => {
  const storedSelectedTimeZone = localStorage.getItem(
    SELECTED_TIMEZONE_LOCALSTORAGE_KEY,
  ) as EAppTimezones;

  // Ensure the stored timezone is a valid EAppTimezones value; fallback to EST if not.
  const selectedTimeZone = Object.values(EAppTimezones).includes(
    storedSelectedTimeZone,
  )
    ? storedSelectedTimeZone
    : EAppTimezones.EST;

  if (!date || !moment(date, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  return formatTimeByTimezone(selectedTimeZone, date.toString());
};

/**
 * Formats a date to the selected timezone or returns '-' if date is not valid or not provided.
 *
 * @param date - Date string or Date object to be formatted.
 * @returns Formatted date string or a hyphen '-' if date is invalid or not provided.
 */
export const getDateTimeAgo = (date?: string | Date): string => {
  const storedSelectedTimeZone = localStorage.getItem(
    SELECTED_TIMEZONE_LOCALSTORAGE_KEY,
  ) as EAppTimezones;

  // Ensure the stored timezone is a valid EAppTimezones value; fallback to EST if not.
  const selectedTimeZone = Object.values(EAppTimezones).includes(
    storedSelectedTimeZone,
  )
    ? storedSelectedTimeZone
    : EAppTimezones.EST;

  if (!date || !moment(date, moment.ISO_8601, true).isValid()) {
    return '-';
  }

  return formatTimeAgoByTimezone(selectedTimeZone, date.toString());
};

type IProcessedTimeProps = Date | string;

export const getProcessedTime = (
  startDate: IProcessedTimeProps,
  endDate: IProcessedTimeProps,
) => {
  // Calculate the duration between createdOn and processedOn
  const duration = moment.duration(moment(endDate).diff(moment(startDate)));

  // Extract days, hours, minutes, and seconds
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  // Format the duration
  const formattedDuration = [
    days > 0 ? `${days}d` : '',
    hours > 0 ? `${hours}h` : '',
    minutes > 0 ? `${minutes}m` : '',
    seconds > 0 ? `${seconds}s` : '',
  ]
    .filter(Boolean)
    .join(' ');
  return formattedDuration;
};

export const getSelectedTimeZone = () => {
  const timezonesDropdownList = Object.entries(EAppTimezones).map(
    ([key, value], index) => ({
      id: (index + 1).toString(),
      label: key,
      value,
    }),
  );

  const storedSelectedTimeZone =
    localStorage.getItem(SELECTED_TIMEZONE_LOCALSTORAGE_KEY) ||
    EAppTimezones.EST;

  const initialTimeZone =
    timezonesDropdownList.find((tz) => tz.value === storedSelectedTimeZone) ||
    timezonesDropdownList[1];
  return initialTimeZone.value;
};

export const applyTimeZoneToSelectedCustomDate = (
  date: string,
  format: string,
  selectedTimeZone: string,
  start: boolean,
) => {
  return start
    ? moment.tz(date, format, selectedTimeZone).startOf('day').format()
    : moment.tz(date, format, selectedTimeZone).endOf('day').format();
};

export const getTimezoneAbbreviation = (
  timeZoneValue: string,
  timestamp?: number | string,
): string => {
  // If no timestamp is provided, use the current time
  const time = timestamp ? moment(timestamp) : moment();
  // .tz(zone).format('z') returns the specific abbreviation (EST, EDT, CST, CDT, etc.)
  // for that exact point in time in that specific geographic region.
  return moment.tz(time, timeZoneValue).format('z');
};

/**
 * Returns the default date range for the dashboard (Last 7 Days)
 * aligned to the start of the first day and end of today.
 */
export const getDefaultDashboardRange = (timezone: string) => {
  return {
    startDate: moment()
      .subtract(6, 'days')
      .tz(timezone)
      .startOf('day')
      .format(),
    endDate: moment().tz(timezone).endOf('day').format(),
  };
};
